import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { AE_BTN_SECONDARY, AE_CARD_COMPACT, AE_MUTED } from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeCampaign, AeResponseSummary } from '@/types/assessmentEngine';
import { CampaignResponseDetailModal } from './CampaignResponseDetailModal';

interface ResponsesTabProps {
  campaign: AeCampaign;
}

/** Campaign hub — submitted assessment responses and aggregate actions. */
export const ResponsesTab: React.FC<ResponsesTabProps> = ({ campaign }) => {
  const campaignId = campaign.id;
  const isAnonymous = campaign.is_anonymous;
  const { t } = useLanguage();
  const [responses, setResponses] = useState<AeResponseSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [viewResponseId, setViewResponseId] = useState<string | null>(null);

  const isSubmitted = (status: string) => status === 'complete' || status === 'incomplete';

  const loadResponses = async () => {
    const responseList = await assessmentEngineService.listResponses(campaignId);
    setResponses(responseList);
  };

  useEffect(() => {
    loadResponses()
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [campaignId]);

  if (loading) {
    return <p className={`${AE_MUTED} p-4`}>{t('assessmentEngine.hub.loadingResponses')}</p>;
  }

  return (
    <div className="space-y-3">
      {viewResponseId && (
        <CampaignResponseDetailModal
          campaignId={campaignId}
          responseId={viewResponseId}
          isAnonymous={isAnonymous}
          onClose={() => setViewResponseId(null)}
          onUpdated={loadResponses}
        />
      )}

      <div className={`${AE_CARD_COMPACT} overflow-hidden p-0`}>
        <h3 className="text-sm font-semibold text-[var(--color-surface-text)] px-3 pt-3 pb-1.5">
          {t('assessmentEngine.hub.responses')}
        </h3>
        {isAnonymous && (
          <p className="text-xs text-[var(--color-warning)] bg-[var(--color-warning)]/10 px-3 py-1.5">
            {t('assessmentEngine.hub.anonymousNote')}
          </p>
        )}
        {responses.length === 0 ? (
          <p className={`p-4 pt-2 ${AE_MUTED}`}>{t('assessmentEngine.hub.noResponses')}</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-left text-[var(--color-surface-text)] bg-[var(--color-hover-bg)]">
                {!isAnonymous && <th className="px-3 py-1.5">{t('assessmentEngine.hub.email')}</th>}
                {!isAnonymous && (
                  <th className="px-3 py-1.5">{t('assessmentEngine.hub.emailDomain')}</th>
                )}
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.timePeriod')}</th>
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.responseStatus')}</th>
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.started')}</th>
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.completed')}</th>
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.aggregateStatus')}</th>
                <th className="px-3 py-1.5">{t('assessmentEngine.hub.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {responses.map((r) => (
                <tr
                  key={r.id}
                  className={`border-b border-[var(--color-border)] ${
                    r.excluded_from_aggregate ? 'opacity-60' : ''
                  }`}
                >
                  {!isAnonymous && (
                    <td className="px-3 py-1.5 text-[var(--color-surface-text)]">{r.respondent_email ?? '—'}</td>
                  )}
                  {!isAnonymous && (
                    <td className="px-3 py-1.5 text-[var(--color-surface-text)]">
                      {r.email_domain ?? '—'}
                    </td>
                  )}
                  <td className="px-3 py-1.5 text-[var(--color-surface-text)]">
                    {r.time_period_key || '—'}
                  </td>
                  <td className="px-3 py-1.5 text-[var(--color-surface-text)]">{r.status}</td>
                  <td className="px-3 py-1.5 text-[var(--color-surface-text)]">
                    {r.started_at ? new Date(r.started_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-[var(--color-surface-text)]">
                    {r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-[var(--color-surface-text)]">
                    {r.excluded_from_aggregate
                      ? t('assessmentEngine.hub.excludedFromAverage')
                      : isSubmitted(r.status)
                        ? t('assessmentEngine.hub.countedInAverage')
                        : '—'}
                  </td>
                  <td className="px-3 py-1.5 space-x-2">
                    <button
                      type="button"
                      className={`text-xs ${AE_BTN_SECONDARY} py-1 px-2`}
                      onClick={() => setViewResponseId(r.id)}
                    >
                      {t('assessmentEngine.hub.viewResponse')}
                    </button>
                    {isSubmitted(r.status) && (
                      <button
                        type="button"
                        disabled={actionBusyId === r.id}
                        className={`text-xs ${AE_BTN_SECONDARY} py-1 px-2`}
                        onClick={async () => {
                          setActionBusyId(r.id);
                          try {
                            if (r.excluded_from_aggregate) {
                              await assessmentEngineService.restoreResponse(campaignId, r.id);
                              toast.success(t('assessmentEngine.hub.responseRestored'));
                            } else {
                              await assessmentEngineService.invalidateResponse(campaignId, r.id);
                              toast.success(t('assessmentEngine.hub.responseInvalidated'));
                            }
                            await loadResponses();
                          } catch {
                            toast.error(t('assessmentEngine.hub.responseActionFailed'));
                          } finally {
                            setActionBusyId(null);
                          }
                        }}
                      >
                        {r.excluded_from_aggregate
                          ? t('assessmentEngine.hub.restoreResponse')
                          : t('assessmentEngine.hub.invalidateResponse')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
